import { useMemo, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Topbar } from "../components/Topbar";
import { KpiCard } from "../components/KpiCard";
import { Panel } from "../components/Panel";
import { LoadingState, ErrorState } from "../components/States";
import { useDashboard } from "../hooks/useDashboard";
import { formatCurrency, formatHours, formatNumber, formatPct } from "../lib/dates";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from "recharts";
import {
  Search, CheckCircle2, AlertCircle, Clock, TrendingUp, TrendingDown, Settings2, Download,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

const STATUS_LABELS = {
  en_plazo: { label: "En plazo", color: "#10B981", icon: Clock, bg: "bg-emerald-50", text: "text-emerald-700" },
  fuera_plazo: { label: "Fuera de plazo", color: "#EF4444", icon: AlertCircle, bg: "bg-red-50", text: "text-red-700" },
  terminado: { label: "Terminado", color: "#94A3B8", icon: CheckCircle2, bg: "bg-slate-100", text: "text-slate-600" },
};

const ALLOCATION_RULES = [
  { value: "income", label: "Por ingresos directos" },
  { value: "hours", label: "Por horas imputadas" },
  { value: "employees", label: "Por nº de empleados" },
  { value: "equal", label: "Reparto equitativo" },
];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LABELS[status] || STATUS_LABELS.terminado;
  const Icon = cfg.icon;
  return (
    <span
      className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium", cfg.bg, cfg.text)}
      data-testid={`status-badge-${status}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

const Money = ({ value, accent }) => {
  const cls =
    accent === "positive"
      ? "text-emerald-600"
      : accent === "negative"
      ? "text-red-600"
      : accent === "warning"
      ? "text-amber-700"
      : "text-slate-700";
  return <span className={cn("tabular-nums", cls)}>{formatCurrency(value)}</span>;
};

// ----- CSV export -----
const exportCSV = (rows) => {
  const cols = [
    "Proyecto", "Estado", "Cliente",
    "Ingresos Pedido", "Ingresos Facturado", "Ingresos Por facturar",
    "Compras Pedido", "Compras Facturado", "Compras Por facturar",
    "Margen Pedido", "Margen Facturado", "Margen Por facturar",
    "Costes directos", "Costes indirectos", "Horas",
  ];
  const head = cols.map((c) => `"${c}"`).join(",");
  const body = rows.map((r) => [
    r.name, r.status, r.customer || "",
    r.ingresos_pedido, r.ingresos_facturado, r.ingresos_por_facturar,
    r.compras_pedido, r.compras_facturado, r.compras_por_facturar,
    r.margen_pedido, r.margen_facturado, r.margen_por_facturar,
    r.costes_directos, r.costes_indirectos, r.horas,
  ].map((v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  }).join(",")).join("\n");
  const csv = "\uFEFF" + head + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "rentabilidad_proyectos.csv";
  a.click();
  URL.revokeObjectURL(url);
};

export default function Rentabilidad() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // Allocation rule state (querystring-driven so it survives refresh)
  const initialRule = params.get("rule") || "income";
  const [rule, setRule] = useState(initialRule);
  useEffect(() => {
    if (params.get("rule") !== rule) {
      const next = new URLSearchParams(params);
      next.set("rule", rule);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule]);

  const { data, loading, error } = useDashboard("projects_profitability", { allocation_rule: rule });

  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [marginFilter, setMarginFilter] = useState("all");

  // Drilldown via account: ?account=NAME
  const accountFilter = params.get("account") || "";

  const filteredRows = useMemo(() => {
    let rows = data?.projects || [];
    if (filter) {
      const f = filter.toLowerCase();
      rows = rows.filter((r) =>
        r.name.toLowerCase().includes(f) ||
        (r.customer || "").toLowerCase().includes(f) ||
        (r.manager || "").toLowerCase().includes(f)
      );
    }
    if (accountFilter) {
      const af = accountFilter.toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(af));
    }
    if (statusFilter !== "all") rows = rows.filter((r) => r.status === statusFilter);
    if (marginFilter === "positive") rows = rows.filter((r) => r.margen_facturado >= 0);
    if (marginFilter === "negative") rows = rows.filter((r) => r.margen_facturado < 0);
    return rows;
  }, [data, filter, statusFilter, marginFilter, accountFilter]);

  const statusPie = useMemo(() => {
    if (!data) return [];
    return [
      { name: "En plazo", value: data.kpis.en_plazo, color: STATUS_LABELS.en_plazo.color },
      { name: "Fuera de plazo", value: data.kpis.fuera_plazo, color: STATUS_LABELS.fuera_plazo.color },
      { name: "Terminado", value: data.kpis.terminado, color: STATUS_LABELS.terminado.color },
    ].filter((x) => x.value > 0);
  }, [data]);

  const marginPie = useMemo(() => {
    if (!data) return [];
    return [
      { name: "Con margen", value: data.kpis.con_margen, color: "#10B981" },
      { name: "Sin margen", value: data.kpis.sin_margen, color: "#EF4444" },
    ].filter((x) => x.value > 0);
  }, [data]);

  const clearAccountFilter = () => {
    const next = new URLSearchParams(params);
    next.delete("account");
    setParams(next, { replace: true });
  };

  return (
    <>
      <Topbar
        title="Rentabilidad proyectos"
        subtitle="Matriz Pedido · Facturado · Por facturar (Ingresos / Compras / Margen) y costes indirectos repartidos"
      />
      <div className="p-6 space-y-6" data-testid="rentabilidad-page">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {data && (
          <>
            {accountFilter && (
              <div className="flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-blue-900" data-testid="account-filter-banner">
                <span>Filtrando por cuenta analítica: <strong>{accountFilter}</strong></span>
                <button onClick={clearAccountFilter} className="text-blue-700 underline" data-testid="clear-account-filter">
                  Quitar filtro
                </button>
              </div>
            )}

            {/* Matriz Ingresos × Compras × Margen × Pedido / Facturado / Por facturar */}
            <Panel
              title="Matriz de rentabilidad"
              subtitle="Totales del grupo en el rango seleccionado"
              testId="panel-matrix"
              action={
                <div className="flex items-center gap-2">
                  <Settings2 className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                    Reparto indirectos
                  </span>
                  <select
                    value={rule}
                    onChange={(e) => setRule(e.target.value)}
                    data-testid="allocation-rule-select"
                    className="h-8 text-xs border border-slate-200 rounded px-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1"
                  >
                    {ALLOCATION_RULES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              }
            >
              <div className="grid grid-cols-4 border-t border-l border-slate-200" data-testid="matrix-grid">
                {/* Header row */}
                <div className="bg-slate-50 px-4 py-2 border-r border-b border-slate-200 text-[11px] uppercase tracking-wide font-semibold text-slate-500"></div>
                <div className="bg-slate-50 px-4 py-2 border-r border-b border-slate-200 text-[11px] uppercase tracking-wide font-semibold text-slate-500 text-right">Pedido</div>
                <div className="bg-slate-50 px-4 py-2 border-r border-b border-slate-200 text-[11px] uppercase tracking-wide font-semibold text-slate-500 text-right">Facturado</div>
                <div className="bg-slate-50 px-4 py-2 border-r border-b border-slate-200 text-[11px] uppercase tracking-wide font-semibold text-slate-500 text-right">Por facturar</div>

                {/* Ingresos row */}
                <div className="px-4 py-3 border-r border-b border-slate-200 font-medium text-slate-800 text-sm">Ingresos</div>
                <div className="px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-slate-900 text-base font-medium" data-testid="matrix-ingresos-pedido">{formatCurrency(data.kpis.ingresos_pedido)}</div>
                <div className="px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-emerald-700 text-base font-medium" data-testid="matrix-ingresos-facturado">{formatCurrency(data.kpis.ingresos_facturado)}</div>
                <div className="px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-amber-700 text-base font-medium" data-testid="matrix-ingresos-por-facturar">{formatCurrency(data.kpis.ingresos_por_facturar)}</div>

                {/* Compras row */}
                <div className="px-4 py-3 border-r border-b border-slate-200 font-medium text-slate-800 text-sm">Compras y gastos</div>
                <div className="px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-slate-900 text-base font-medium" data-testid="matrix-compras-pedido">{formatCurrency(data.kpis.compras_pedido)}</div>
                <div className="px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-slate-900 text-base font-medium" data-testid="matrix-compras-facturado">{formatCurrency(data.kpis.compras_facturado)}</div>
                <div className="px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-amber-700 text-base font-medium" data-testid="matrix-compras-por-facturar">{formatCurrency(data.kpis.compras_por_facturar)}</div>

                {/* Margen row */}
                <div className="bg-slate-50 px-4 py-3 border-r border-b border-slate-200 font-semibold text-slate-900 text-sm">Margen</div>
                <div className={cn("bg-slate-50 px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-base font-semibold", data.kpis.margen_pedido >= 0 ? "text-emerald-600" : "text-red-600")} data-testid="matrix-margen-pedido">
                  {formatCurrency(data.kpis.margen_pedido)}
                  <span className="block text-[10px] font-normal text-slate-500">{formatPct(data.kpis.margen_pedido_pct)}</span>
                </div>
                <div className={cn("bg-slate-50 px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-base font-semibold", data.kpis.margen_facturado >= 0 ? "text-emerald-600" : "text-red-600")} data-testid="matrix-margen-facturado">
                  {formatCurrency(data.kpis.margen_facturado)}
                  <span className="block text-[10px] font-normal text-slate-500">{formatPct(data.kpis.margen_facturado_pct)}</span>
                </div>
                <div className={cn("bg-slate-50 px-4 py-3 border-r border-b border-slate-200 text-right tabular-nums text-base font-semibold", data.kpis.margen_por_facturar >= 0 ? "text-emerald-600" : "text-red-600")} data-testid="matrix-margen-por-facturar">
                  {formatCurrency(data.kpis.margen_por_facturar)}
                </div>
              </div>
            </Panel>

            {/* Status KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <KpiCard testId="kpi-rent-total" label="Proyectos" value={formatNumber(data.kpis.total_projects, { maximumFractionDigits: 0 })} />
              <KpiCard testId="kpi-rent-en-plazo" label="En plazo" value={formatNumber(data.kpis.en_plazo, { maximumFractionDigits: 0 })} accent="success" />
              <KpiCard testId="kpi-rent-fuera-plazo" label="Fuera de plazo" value={formatNumber(data.kpis.fuera_plazo, { maximumFractionDigits: 0 })} accent="danger" />
              <KpiCard testId="kpi-rent-terminado" label="Terminados" value={formatNumber(data.kpis.terminado, { maximumFractionDigits: 0 })} />
              <KpiCard testId="kpi-rent-indirectos" label="C. indirectos repartidos" value={formatCurrency(data.kpis.costes_indirectos)} sublabel={`Regla: ${ALLOCATION_RULES.find(r => r.value === rule)?.label}`} accent="warning" />
              <KpiCard testId="kpi-rent-horas" label="Horas imputadas" value={formatHours(data.kpis.horas)} />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel title="Estado de plazo" testId="chart-status">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45}
                           label={(e) => `${e.name}: ${e.value}`}>
                        {statusPie.map((e, i) => (<Cell key={i} fill={e.color} />))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
              <Panel title="Margen facturado" subtitle="Proyectos con margen positivo vs negativo" testId="chart-margin">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={marginPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45}
                           label={(e) => `${e.name}: ${e.value}`}>
                        {marginPie.map((e, i) => (<Cell key={i} fill={e.color} />))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            {/* Detail table with grouped headers */}
            <Panel
              title="Detalle por proyecto"
              subtitle={`${filteredRows.length} proyectos con actividad en el período`}
              testId="panel-projects-table"
              action={
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Buscar proyecto, cliente..."
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="h-8 text-xs pl-8 w-56"
                      data-testid="project-search"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="filter-status"
                    className="h-8 text-xs border border-slate-200 rounded px-2 bg-white text-slate-700"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="en_plazo">En plazo</option>
                    <option value="fuera_plazo">Fuera de plazo</option>
                    <option value="terminado">Terminado</option>
                  </select>
                  <select
                    value={marginFilter}
                    onChange={(e) => setMarginFilter(e.target.value)}
                    data-testid="filter-margin"
                    className="h-8 text-xs border border-slate-200 rounded px-2 bg-white text-slate-700"
                  >
                    <option value="all">Todos los márgenes</option>
                    <option value="positive">Con margen</option>
                    <option value="negative">Sin margen</option>
                  </select>
                  <Button variant="outline" size="sm" onClick={() => exportCSV(filteredRows)} data-testid="export-csv-button" className="h-8 text-xs">
                    <Download className="w-3.5 h-3.5 mr-1.5" />Exportar CSV
                  </Button>
                </div>
              }
            >
              <div className="overflow-x-auto rounded border border-slate-200">
                <table className="w-full text-sm" data-testid="rent-projects-table">
                  <thead>
                    <tr className="bg-slate-50 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-600">
                      <th className="text-left px-3 py-2 border-b border-r border-slate-200" rowSpan={2}>Proyecto</th>
                      <th className="text-left px-3 py-2 border-b border-r border-slate-200" rowSpan={2}>Estado</th>
                      <th className="text-left px-3 py-2 border-b border-r border-slate-200" rowSpan={2}>Cliente</th>
                      <th className="text-center px-3 py-1 border-b border-r border-slate-200 bg-blue-50 text-blue-900" colSpan={3}>Ingresos</th>
                      <th className="text-center px-3 py-1 border-b border-r border-slate-200 bg-amber-50 text-amber-900" colSpan={3}>Compras y gastos</th>
                      <th className="text-center px-3 py-1 border-b border-r border-slate-200 bg-emerald-50 text-emerald-900" colSpan={3}>Margen</th>
                      <th className="text-right px-3 py-2 border-b border-slate-200" rowSpan={2}>Horas</th>
                    </tr>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-[0.04em] font-semibold text-slate-500">
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Pedido</th>
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Facturado</th>
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Por fact.</th>
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Pedido</th>
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Facturado</th>
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Por fact.</th>
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Pedido</th>
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Facturado</th>
                      <th className="text-right px-3 py-1 border-b border-r border-slate-200">Por fact.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 && (
                      <tr><td colSpan={13} className="text-center py-8 text-slate-400 text-sm">Sin proyectos en el rango / filtros aplicados</td></tr>
                    )}
                    {filteredRows.map((r, i) => (
                      <tr key={r.id} className={cn("border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-150", i % 2 ? "bg-slate-50/30" : "")} data-testid={`rent-row-${r.id}`}>
                        <td className="px-3 py-2 text-sm border-r border-slate-100 max-w-[280px] truncate font-medium text-slate-800">{r.name}</td>
                        <td className="px-3 py-2 text-sm border-r border-slate-100"><StatusBadge status={r.status} /></td>
                        <td className="px-3 py-2 text-sm border-r border-slate-100 max-w-[180px] truncate text-slate-600">{r.customer || "—"}</td>
                        <td className="px-3 py-2 text-sm border-r border-slate-100 text-right"><Money value={r.ingresos_pedido} /></td>
                        <td className="px-3 py-2 text-sm border-r border-slate-100 text-right"><Money value={r.ingresos_facturado} accent="positive" /></td>
                        <td className="px-3 py-2 text-sm border-r border-slate-100 text-right"><Money value={r.ingresos_por_facturar} accent="warning" /></td>
                        <td className="px-3 py-2 text-sm border-r border-slate-100 text-right"><Money value={r.compras_pedido} /></td>
                        <td className="px-3 py-2 text-sm border-r border-slate-100 text-right"><Money value={r.compras_facturado} /></td>
                        <td className="px-3 py-2 text-sm border-r border-slate-100 text-right"><Money value={r.compras_por_facturar} accent="warning" /></td>
                        <td className={cn("px-3 py-2 text-sm border-r border-slate-100 text-right tabular-nums font-medium", r.margen_pedido >= 0 ? "text-emerald-600" : "text-red-600")}>
                          <span className="inline-flex items-center gap-1">
                            {r.margen_pedido >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {formatCurrency(r.margen_pedido)}
                          </span>
                        </td>
                        <td className={cn("px-3 py-2 text-sm border-r border-slate-100 text-right tabular-nums font-medium", r.margen_facturado >= 0 ? "text-emerald-600" : "text-red-600")}>
                          {formatCurrency(r.margen_facturado)}
                        </td>
                        <td className={cn("px-3 py-2 text-sm border-r border-slate-100 text-right tabular-nums font-medium", r.margen_por_facturar >= 0 ? "text-emerald-600" : "text-red-600")}>
                          {formatCurrency(r.margen_por_facturar)}
                        </td>
                        <td className="px-3 py-2 text-sm text-right text-slate-500">{formatHours(r.horas)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-xs text-blue-900 space-y-1">
              <div><strong>Cálculos:</strong></div>
              <ul className="list-disc pl-5 space-y-0.5">
                <li><em>Pedido</em> (Ingresos / Compras): suma de líneas de pedido (sale.order.line / purchase.order.line) en estado confirmado, ponderadas por su <code>analytic_distribution</code> sobre la cuenta analítica del proyecto.</li>
                <li><em>Facturado</em>: apuntes analíticos generados al contabilizar facturas (positivos = ingresos, negativos = compras/gastos).</li>
                <li><em>Por facturar</em>: <code>max(Pedido − Facturado, 0)</code>.</li>
                <li><em>Margen</em>: <code>Ingresos − Compras</code> en cada estado.</li>
                <li><em>Costes indirectos repartidos</em>: planes "COSTES INDIRECTOS" del mismo departamento, prorrateados por la regla seleccionada (ingresos / horas / nº empleados / equitativo).</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </>
  );
}
